import React, { useState } from "react";
import { fetchsubmittable, fetchaddArn, fetchnewarn } from "./Arnkpi";

const ArnModel = ({ isOpen, onClose, id }) => {
    const typeOptions = [
        { value: "arn", label: "ARN" },
        { value: "reg", label: "Registration Number" },
        { value: "chassis", label: "Chassis Number" },
    ];

    const [selectedType, setSelectedType] = useState(typeOptions[0].value);
    const [inputValue, setInputValue] = useState(""); // store as string
    const [showTable, setShowTable] = useState(false);
    const [arnTableData, setArnTableData] = useState([]);
    const [remark, setRemark] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(""); // error state

    const handleCheck = async (values) => {
        if (!values || values.length === 0) return [];
        let validValues = [];

        for (let val of values) {
            if (val === id) {
                setErrorMessage(`❌ ${val} cannot be added because it is the same as the Parent ARN.`);
                continue;
            }

            try {
                const payload = { inputValue: [val], inputType: selectedType };
                const res = await fetchaddArn(payload);

                if (Array.isArray(res)) {
                    const match = res.find((item) => {
                        if (selectedType === "arn") {
                            return item.ACCNT_ARN === val && item.PAR_ACCNT_ARN === item.ACCNT_ARN;
                        }
                        if (selectedType === "reg" || selectedType === "chassis") {
                            return item.PAR_ACCNT_ARN === item.ACCNT_ARN;
                        }
                        return false;
                    });

                    if (match) {
                        setErrorMessage(`❌ ${val} cannot be added because it is already mapped to the same Parent ARN.`);
                        continue;
                    }
                }
            } catch (error) {
                console.error("Error validating value:", val, error);
                setErrorMessage(`Validation failed for ${val}`);
                continue;
            }

            validValues.push(val);
        }

        return validValues;
    };

    const handleNext = async () => {
        setErrorMessage("");

        // split comma-separated values into array
        const values = inputValue
            .split(",")
            .map((v) => v.trim())
            .filter((v) => v !== "");

        if (values.length === 0) return;

        const validValues = await handleCheck(values);
        if (!validValues || validValues.length === 0) return;

        try {
            const payload = { parArn: id, inputValue: validValues, inputType: selectedType };
            const res = await fetchsubmittable(payload);

            // ensure always an array
            const resArray = Array.isArray(res) ? res : [res];

            // map each valid child value to its matching response
            const newRows = validValues.map((val, index) => {
                // try to find matching API response by ARN/reg/chassis
                const item =
                    resArray.find(
                        (r) =>
                            r?.ACCNT_ARN === val ||
                            r?.children_arn === val ||
                            r?.Vehicle_Registration_Number === val ||
                            r?.Chassis_number === val
                    ) || resArray[index] || {}; // fallback: take by index

                const resolvedArn = item?.children_arn || item?.ACCNT_ARN || val;
                const status = item?.status == null || item?.status === "" ? "New" : item?.status;

                return {
                    id: arnTableData.length + index + 1,
                    status,
                    childValue: val,
                    childArn: resolvedArn,
                    existingParent: item?.existing_parent || "",
                    requestedParent: item?.requested_parent || id,
                    accName: item?.account_name || "",
                    lastPurchaseQnt: item?.last_purchase_qty || "",
                    lastPurchaseDt: item?.last_purchase_date || "",
                    lastPurchaseDealership: item?.last_purchase_dealership || "",
                };
            });

            setArnTableData((prev) => [...prev, ...newRows]);
            setShowTable(true);
            setInputValue(""); // clear textarea
        } catch (error) {
            console.error("Error submitting:", error);
            setErrorMessage("Something went wrong ❌");
        }
    };



    const handleFinalSubmit = async () => {
        if (arnTableData.length === 0) {
            setErrorMessage("No data to submit");
            return;
        }

        setIsSubmitting(true);
        try {
            const userDetailsToken = localStorage.getItem("userDetailsToken");
            const childValues = arnTableData.map((row) => row.childValue);

            const payload = {
                parArn: id,
                userDetailsToken,
                inputType: selectedType,
                inputValue: childValues,
                remark: remark || "",
            };

            const result = await fetchnewarn(payload);
            if (result && Array.isArray(result)) {
                alert(`✅ Successfully submitted `);
                handleReset();
                onClose();
                return;
            }
        } catch (error) {
            console.error("Error during final submission:", error);
            setErrorMessage("Final submission failed ❌");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReset = () => {
        setInputValue("");
        setSelectedType(typeOptions[0].value);
        setShowTable(false);
        setArnTableData([]);
        setRemark("");
        setErrorMessage("");
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div
                className={`bg-white rounded-lg shadow-xl mx-4 ${showTable
                    ? "w-full max-w-5xl max-h-[90vh] overflow-x-auto"
                    : "w-full max-w-md"
                    }`}
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-6 py-4 rounded-t-lg relative">
                    <button
                        onClick={() => {
                            handleReset();
                            onClose();
                        }}
                        className="absolute top-3 right-3 text-white hover:text-gray-200 text-xl font-bold"
                    >
                        ×
                    </button>
                    <h2 className="text-xl font-semibold">
                        {showTable ? "Details" : "Enter Child Details"}
                    </h2>
                </div>

                {!showTable ? (
                    <div className="p-6">
                        {errorMessage && (
                            <div className="mb-4 p-3 text-sm text-red-700 bg-red-100 border border-red-300 rounded">
                                {errorMessage}
                            </div>
                        )}

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                            <p className="text-blue-700 text-sm">
                                <strong>Note:</strong> Enter Child{" "}
                                {typeOptions.find((t) => t.value === selectedType)?.label}/s to
                                be mapped to Parent {id}.
                            </p>
                            <p className="text-blue-600 text-xs mt-1">
                                (For multiple entries, enter comma separated values).
                            </p>
                        </div>

                        <div className="space-y-3 mb-6">
                            {typeOptions.map((type) => (
                                <label key={type.value} className="flex items-center cursor-pointer">
                                    <input
                                        type="radio"
                                        name="arnType"
                                        value={type.value}
                                        checked={selectedType === type.value}
                                        onChange={(e) => setSelectedType(e.target.value)}
                                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="ml-2 text-sm font-medium text-gray-700">
                                        {type.label}
                                    </span>
                                </label>
                            ))}
                        </div>

                        {/* Textarea */}
                        <textarea
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder={`Enter Child ${typeOptions.find((t) => t.value === selectedType)?.label
                                } here (comma separated for multiple)`}
                            className="w-full h-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none mb-6"
                        />

                        <div className="flex gap-3">
                            <button
                                onClick={handleNext}
                                disabled={inputValue.trim().length === 0}
                                className={`flex-1 py-2 px-4 rounded-md text-white font-medium ${inputValue.trim().length === 0
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600"
                                    }`}
                            >
                                Next
                            </button>
                            <button
                                onClick={handleReset}
                                className="flex-1 bg-gray-400 text-white py-2 px-4 rounded-md hover:bg-gray-500"
                            >
                                Reset
                            </button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div className="overflow-x-auto overflow-y-auto max-h-96">
                            <table className="w-full">
                                <thead className="bg-sky-900 text-white sticky top-0">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium">Child ARN</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium">Existing Parent</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium">Requested Parent</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium">Acc Name</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium">Last Purchase Qnt</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium">Last Purchase Dt</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium">Last Purchase Dealership</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white">
                                    {arnTableData.map((row, index) => (
                                        <tr
                                            key={row.id}
                                            className={`border-b border-gray-200 hover:bg-gray-50 ${index % 2 === 0 ? "bg-white" : "bg-gray-50"
                                                }`}
                                        >
                                            <td className="px-4 py-3 text-sm text-gray-700">{row.status}</td>
                                            <td className="px-4 py-3 text-sm text-gray-700">{row.childArn}</td>
                                            <td className="px-4 py-3 text-sm text-gray-700">{row.existingParent}</td>
                                            <td className="px-4 py-3 text-sm text-gray-700">{row.requestedParent}</td>
                                            <td className="px-4 py-3 text-sm text-gray-700">{row.accName}</td>
                                            <td className="px-4 py-3 text-sm text-gray-700">{row.lastPurchaseQnt}</td>
                                            <td className="px-4 py-3 text-sm text-gray-700">{row.lastPurchaseDt}</td>
                                            <td className="px-4 py-3 text-sm text-gray-700">{row.lastPurchaseDealership}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="px-6 py-4 border-t border-gray-200">
                            <label htmlFor="remark" className="block text-sm font-medium text-gray-700 mb-2">
                                Remark
                            </label>
                            <textarea
                                id="remark"
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter your remarks here..."
                                value={remark}
                                onChange={(e) => setRemark(e.target.value)}
                            />
                        </div>

                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                            <button
                                onClick={handleFinalSubmit}
                                disabled={isSubmitting || arnTableData.length === 0}
                                className={`px-6 py-2 rounded-md text-white font-medium ${isSubmitting || arnTableData.length === 0
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700"
                                    }`}
                            >
                                {isSubmitting ? "Submitting..." : "Submit"}
                            </button>
                            <button
                                onClick={() => setShowTable(false)}
                                disabled={isSubmitting}
                                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-md hover:from-blue-600 hover:to-blue-700 disabled:opacity-50"
                            >
                                Back
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ArnModel;
